import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../../App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownBadge } from "./DropdownHooks";
import { ArrowRight, Clock, User, MessageSquare, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TRANSITION_MAP = {
  draft: ["under_review"],
  under_review: ["approved", "draft"],
  approved: ["effective", "under_review"],
  effective: ["superseded", "obsolete"],
  superseded: ["archived"],
  obsolete: ["archived"],
  archived: [],
};

const TRANSITION_LABELS = {
  under_review: "Submit for Review",
  approved: "Approve",
  draft: "Return to Draft",
  effective: "Make Effective",
  superseded: "Supersede",
  obsolete: "Mark Obsolete",
  archived: "Archive",
};

const TRANSITION_COLORS = {
  under_review: "#F59E0B",
  approved: "#10B981",
  draft: "#94A3B8",
  effective: "#2563EB",
  superseded: "#D97706",
  obsolete: "#DC2626",
  archived: "#64748B",
};

export function LifecycleBadge({ state }) {
  if (!state) return null;
  return <DropdownBadge dropdownKey="document_lifecycle_states" value={state} />;
}

export function LifecycleBar({ blueprint, onTransition }) {
  const currentState = blueprint?.lifecycle_state || "draft";
  const nextStates = TRANSITION_MAP[currentState] || [];
  if (nextStates.length === 0 && currentState === "archived") return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="lifecycle-bar">
      {nextStates.map((ns) => (
        <Button
          key={ns}
          size="sm"
          onClick={() => onTransition(blueprint.id, ns)}
          className="rounded-none text-[10px] font-bold text-white"
          style={{ backgroundColor: TRANSITION_COLORS[ns] || "#64748B" }}
          data-testid={`lifecycle-btn-${ns}`}
        >
          <ChevronRight size={10} className="mr-0.5" />
          {TRANSITION_LABELS[ns] || ns}
        </Button>
      ))}
    </div>
  );
}

export function LifecycleTransitionDialog({ open, onClose, blueprint, onConfirm }) {
  const [targetState, setTargetState] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const currentState = blueprint?.lifecycle_state || "draft";
  const nextStates = TRANSITION_MAP[currentState] || [];

  const handleConfirm = async () => {
    if (!targetState) { toast.error("Select a target state"); return; }
    setSubmitting(true);
    try {
      await onConfirm(blueprint.id, targetState, comment);
      setComment("");
      setTargetState("");
      onClose();
    } catch { /* handled by parent */ }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="lifecycle-transition-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F172A]">Document Lifecycle Transition</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Transition "{blueprint?.original_filename}" from {currentState} to a new lifecycle state.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[#64748B]">Current:</span>
            <LifecycleBadge state={currentState} />
            <ArrowRight size={12} className="text-[#94A3B8]" />
            <span className="text-[#64748B]">New:</span>
            {targetState ? <LifecycleBadge state={targetState} /> : <span className="text-[#94A3B8]">Select below</span>}
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Target State</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {nextStates.map((ns) => (
                <button
                  key={ns}
                  onClick={() => setTargetState(ns)}
                  className={`px-3 py-1.5 text-xs font-bold border transition-colors ${targetState === ns ? "text-white" : "text-[#475569] border-[#E2E8F0] bg-white hover:bg-[#F8FAFC]"}`}
                  style={targetState === ns ? { backgroundColor: TRANSITION_COLORS[ns], borderColor: TRANSITION_COLORS[ns] } : {}}
                  data-testid={`transition-option-${ns}`}
                >
                  {TRANSITION_LABELS[ns] || ns}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Comment (optional)</Label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-1 w-full border border-[#E2E8F0] p-2.5 text-sm h-16 resize-none focus:border-[#2563EB] focus:outline-none"
              placeholder="Reason for transition..."
              data-testid="lifecycle-comment"
            />
          </div>
          <Button
            onClick={handleConfirm}
            disabled={!targetState || submitting}
            className="w-full rounded-none text-white text-xs font-bold"
            style={{ backgroundColor: TRANSITION_COLORS[targetState] || "#2563EB" }}
            data-testid="lifecycle-confirm-btn"
          >
            {submitting ? "Processing..." : `Transition to ${TRANSITION_LABELS[targetState] || targetState || "..."}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LifecycleHistoryDialog({ open, onClose, blueprintId }) {
  const [history, setHistory] = useState([]);
  const [currentState, setCurrentState] = useState("draft");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !blueprintId) return;
    setLoading(true);
    axios.get(`${API}/blueprints/${blueprintId}/lifecycle/history`, { headers: getAuthHeaders() })
      .then((r) => {
        setHistory(r.data.history || []);
        setCurrentState(r.data.current_state || "draft");
      })
      .catch(() => toast.error("Failed to load lifecycle history"))
      .finally(() => setLoading(false));
  }, [open, blueprintId]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg max-h-[70vh] overflow-y-auto" data-testid="lifecycle-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F172A]">Document Lifecycle History</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Current state: <LifecycleBadge state={currentState} />
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-[#94A3B8]">Loading...</div>
        ) : history.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#94A3B8]">No transitions yet. Document is in its initial state.</div>
        ) : (
          <div className="space-y-2 mt-2">
            {history.map((h, i) => (
              <div key={h.id || i} className="p-3 border border-[#E2E8F0] bg-[#FAFAFA]" data-testid={`history-entry-${i}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <LifecycleBadge state={h.from_state} />
                  <ArrowRight size={12} className="text-[#94A3B8]" />
                  <LifecycleBadge state={h.to_state} />
                  <span className="ml-auto text-[#94A3B8] flex items-center gap-1"><Clock size={10} />{new Date(h.timestamp).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-[#64748B] mt-1">
                  <User size={10} /> {h.transitioned_by_name}
                </div>
                {h.comment && (
                  <div className="flex items-start gap-1 text-[11px] text-[#475569] mt-1">
                    <MessageSquare size={10} className="mt-0.5 shrink-0" /> {h.comment}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
